/**
 * Connection Display Order Utilities
 *
 * Sorting logic for displaying connections in consistent order
 * across StartPage and Connections Dialog.
 */

import type { Connection } from '../../../types/electron.d';

/**
 * Sort connections with active connections first, then by lastConnectedAt timestamp.
 * Active connections are also sorted by lastConnectedAt within their group.
 *
 * @param connections - Array of connections to sort
 * @param activeConnectionIds - Set of IDs for currently active connections
 * @returns Sorted connections array (new array, original not mutated)
 */
export function sortConnectionsByDisplayOrder(
  connections: Connection[],
  activeConnectionIds: Set<string>
): Connection[] {
  return [...connections].sort((a, b) => {
    const aActive = activeConnectionIds.has(a.id);
    const bActive = activeConnectionIds.has(b.id);

    // Active connections come first
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;

    // Within same group (both active or both inactive), sort by lastConnectedAt
    // New connections (no lastConnectedAt) are treated as "most recent" so they appear at top
    const aTime = a.lastConnectedAt ? new Date(a.lastConnectedAt).getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b.lastConnectedAt ? new Date(b.lastConnectedAt).getTime() : Number.MAX_SAFE_INTEGER;
    return bTime - aTime;
  });
}

/**
 * Get the limited subset of connections to display.
 * - All connected connections are always shown (no limit)
 * - Up to maxDisconnected disconnected connections are shown
 * - "X more connections" only appears when there are more than maxDisconnected disconnected connections
 *
 * @param connections - Sorted array of connections
 * @param activeConnectionIds - Set of IDs for currently active connections
 * @param maxDisconnected - Maximum number of disconnected connections to show
 * @returns Object with visible connections and remaining count
 */
export function getVisibleConnections(
  connections: Connection[],
  activeConnectionIds: Set<string>,
  maxDisconnected: number
): {
  visible: Connection[];
  remainingCount: number;
  hasMore: boolean;
} {
  const connected = connections.filter((c) => activeConnectionIds.has(c.id));
  const disconnected = connections.filter((c) => !activeConnectionIds.has(c.id));

  const visibleDisconnected = disconnected.slice(0, maxDisconnected);
  const remainingCount = Math.max(0, disconnected.length - maxDisconnected);

  return {
    visible: [...connected, ...visibleDisconnected],
    remainingCount,
    hasMore: remainingCount > 0,
  };
}
